// Port of load_paypal in scripts/loaders.py.
//
// Why we need a specific loader instead of letting generic handle it:
//   1. Date-format ambiguity: PayPal exports use DD/MM/YYYY outside the
//      US and MM/DD/YYYY inside it, with NO header signal. Generic
//      tries MM/DD first; for a CA-style export with mostly low-day
//      values that means rows silently mis-date. The specific loader
//      pre-scans the file for a "day > 12" hint.
//   2. Multi-currency: rows have a Currency column; we split into
//      separate per-currency account labels (paypal, paypal-usd, etc.)
//      rather than collapsing into one.
//   3. Status filter: PayPal ships canceled/pending rows that the user
//      shouldn't see in the main view.
//   4. Transfer suppression: PayPal emits paired "General Card Deposit"
//      / "Bank Deposit" rows for instant-funded payments — only the
//      purchase row is the real transaction; the deposit is internal.

import {
  type Transaction,
  newTransaction,
  makeId,
  parseAmount,
  normalizeVendor,
  isInterPortalTransfer,
} from "../common.js";
import { parseCsv, toDictRows, findHeaderIndex } from "../csv.js";

/** Decide whether a file's dates are DD/MM/YYYY or MM/DD/YYYY.
 *  Returns the format string + an `ambiguous` flag. Logic mirrors
 *  scripts/loaders.py::_detect_paypal_date_format. */
function detectPaypalDateFormat(
  dateStrs: string[],
): { format: "dmy" | "mdy"; ambiguous: boolean } {
  let firstOver12 = false;
  let secondOver12 = false;
  for (const s of dateStrs) {
    const m = /^\s*(\d{1,2})\/(\d{1,2})\/\d{4}\s*$/.exec(s || "");
    if (!m) continue;
    const a = parseInt(m[1], 10);
    const b = parseInt(m[2], 10);
    if (a > 12) firstOver12 = true;
    if (b > 12) secondOver12 = true;
  }
  const ambiguous = firstOver12 && secondOver12;
  if (firstOver12 && !secondOver12) return { format: "dmy", ambiguous: false };
  if (secondOver12 && !firstOver12) return { format: "mdy", ambiguous: false };
  // Both true (ambiguous) or both false (no signal): default to DD/MM
  // — PayPal's default outside the US.
  return { format: "dmy", ambiguous };
}

function parsePaypalDate(s: string, format: "dmy" | "mdy"): string {
  const trimmed = (s || "").trim();
  if (!trimmed) return "";
  // First try the detected format. Then ISO. Then the other slash format.
  // Replicate the order in _paypal_parse_date so behavior matches.
  const slashed = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(trimmed);
  if (slashed) {
    const [, a, b, y] = slashed;
    const day = format === "dmy" ? a : b;
    const mon = format === "dmy" ? b : a;
    const d = parseInt(day, 10);
    const mo = parseInt(mon, 10);
    if (mo >= 1 && mo <= 12 && d >= 1 && d <= 31) {
      return `${y}-${mo.toString().padStart(2, "0")}-${d.toString().padStart(2, "0")}`;
    }
  }
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(trimmed);
  if (iso) {
    const [, y, mo, d] = iso;
    return `${y}-${mo.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }
  return "";
}

// Tx types that PayPal emits as paired bookkeeping entries for an
// instant-funded purchase. They're real database rows but not real
// "money movements" from the user's perspective — the matching
// purchase row IS the expense. Mirrors the list in load_paypal.
const FUNDING_TX_TYPES = [
  "general card deposit",
  "bank deposit",
  "general withdrawal",
  "credit to bank",
  "transfer",
  "add funds from a bank account",
];

export interface PaypalLoadOptions {
  /** Optional advisory-note sink (mirrors process_month.notes). */
  notes?: Array<{ file: string; message: string }>;
}

export async function loadPaypal(
  text: string,
  filename: string,
  options: PaypalLoadOptions = {},
): Promise<Transaction[]> {
  // PayPal exports START with the header row, but bank-CSV-handed-as-
  // PayPal files sometimes have a few preamble metadata lines. Strip
  // anything before a row that contains the expected header markers.
  // Two known schemas:
  //   1. Activity statement export: Date,Time,TimeZone,Name,Type,Status,...
  //   2. Activity download / merchant CSV:
  //        Date,Transaction ID,Type,Status,Currency,Gross,Fee,Net,From Email Address,...
  // Schema 2 has no Name column — we fall back to Item Title / Invoice
  // Number / From Email Address downstream.
  const allRows = parseCsv(text);
  let headerIdx = findHeaderIndex(allRows, ["Date", "Name"]);
  if (headerIdx < 0) headerIdx = findHeaderIndex(allRows, ["Date", "Transaction ID"]);
  if (headerIdx < 0) headerIdx = findHeaderIndex(allRows, ["Date", "Gross"]);
  if (headerIdx < 0) return [];
  const header = allRows[headerIdx].map((h) => (h || "").trim());
  const rows = toDictRows(header, allRows.slice(headerIdx + 1));
  if (rows.length === 0) return [];

  // First pass: detect date format from all rows so we don't mis-route
  // every date in a US-formatted (MM/DD) export.
  const { format, ambiguous } = detectPaypalDateFormat(rows.map((r) => r["Date"] || ""));
  if (ambiguous && options.notes) {
    options.notes.push({
      file: filename,
      message:
        "PayPal date format ambiguous (rows with both day > 12 and month > 12) — defaulted to DD/MM/YYYY. Verify month assignments in the dashboard.",
    });
  }

  const out: Transaction[] = [];
  for (const row of rows) {
    const status = (row["Status"] || "").trim().toLowerCase();
    if (status && status !== "completed" && status !== "approved") continue;

    const txType = (row["Type"] || "").trim().toLowerCase();
    if (FUNDING_TX_TYPES.some((k) => txType.includes(k))) continue;

    // Description: Name is the activity-statement convention; merchant /
    // download exports use Item Title / Invoice Number / From Email Address.
    const description = (
      row["Name"] ||
      row["Description"] ||
      row["Item Title"] ||
      row["Invoice Number"] ||
      row["From Email Address"] ||
      ""
    ).trim();
    const dateStr = parsePaypalDate(row["Date"] || "", format);
    if (!dateStr) continue;

    // PayPal positive = received (income). Project convention is
    // positive = expense, so flip.
    const grossRaw = row["Amount"] || row["Gross"] || row["Net"] || "0";
    const amount = -parseAmount(grossRaw);
    if (amount === 0) continue;

    if (isInterPortalTransfer(description)) continue;

    // Multi-currency: PayPal users with foreign tx have a Currency
    // column. Break out by currency so totals don't summate across
    // currencies. Mirrors Wise.
    const currency = (row["Currency"] || "").trim().toUpperCase();
    const account =
      currency && currency !== "CAD" ? `paypal-${currency.toLowerCase()}` : "paypal";
    const vendorRaw = description || row["Type"] || "PayPal";

    const id = await makeId("paypal", account, dateStr, amount, vendorRaw);
    out.push(
      newTransaction({
        id,
        date: dateStr,
        vendor_raw: vendorRaw,
        vendor_key: normalizeVendor(description) || "paypal",
        amount: Math.round(amount * 100) / 100,
        portal: "paypal",
        account,
        // PayPal is asset-style (your PayPal balance is YOUR money).
        account_kind: "debit",
        source_file: filename,
        raw: { ...row },
      }),
    );
  }
  return out;
}

// Exported for tests.
export const _internal = { detectPaypalDateFormat, parsePaypalDate };
